/**
 * @typedef { import('./reactive-element.js').ReactiveAttributes } ReactiveAttributes
 * @typedef { import('./reactive-element.js').ReactiveHookType } ReactiveHookType
 * @typedef { import('./reactive-element.js').ReactivePrimitiveType } ReactivePrimitiveType
 * @typedef { import('./reactive-element.js').ReactiveProperties } ReactiveProperties
 * @typedef { import('./reactive-element.js').ReactiveType } ReactiveType
 */

import { ReactiveState } from './reactive-state.js';

const PRIMITIVE_TYPES_BY_TYPEOF = {
  string: String,
  boolean: Boolean,
  number: Number,
  object: Object,
};

/**
 * Convert property "value" to attribute value based on "type"
 * @param { unknown } value
 * @param { ReactiveType } type
 * @returns { string }
 */
export function propertyValueToAttribute(value, type) {
  if (type === Object || type === Array) {
    try {
      value = JSON.stringify(value);
    } catch (err) {
      value = '';
    }
    // @ts-ignore
    return value;
  } else if (type === Date) {
    // @ts-ignore
    return value.toJSON();
  }

  const toAttribute = type === Boolean ? toBooleanAttribute : String;
  // @ts-ignore
  return toAttribute(value);
}

/**
 * Convert boolean attribute "value" to enable adding/removing of attribute name
 * @param { unknown } value
 * @returns { '' | null }
 */
function toBooleanAttribute(value) {
  return value ? '' : null;
}

/**
 * Convert attribute "value" to property value based on "type"
 * @param { unknown } value
 * @param { ReactiveType } type
 * @returns { ReturnType<ReactiveType> }
 */
export function attributeValueToProperty(value, type) {
  if (isHookPropertyType(type)) {
    type = getPrimitiveTypeFromValue(value);
  }

  if (type === Object || type === Array) {
    try {
      // @ts-ignore
      return JSON.parse(value);
    } catch (err) {
      return undefined;
    }
  } else if (type === Date) {
    // @ts-ignore
    return new Date(value);
  } else if (type === Boolean) {
    return value !== null;
  } else if (type === String) {
    return value === null ? undefined : value;
  } else if (type === Number) {
    return value === null ? undefined : Number(value);
  }
  // @ts-expect-error: "type" is not a hook that requires 2 params
  return type(value);
}

/**
 * Retrieve the primitive type from "value"
 * @param { unknown } value
 * @returns { ReactivePrimitiveType }
 */
function getPrimitiveTypeFromValue(value) {
  if (value instanceof Date) {
    return Date;
  } else if (Array.isArray(value)) {
    return Array;
  } else {
    // @ts-ignore
    return PRIMITIVE_TYPES_BY_TYPEOF[typeof value] || Object;
  }
}

/**
 * Determine if "type" is a hook type
 * @param { unknown } type
 * @returns { type is ReactiveHookType }
 */
export function isHookPropertyType(type) {
  if (
    type !== String &&
    type !== Boolean &&
    type !== Number &&
    type !== Object &&
    type !== Array &&
    type !== Date &&
    type !== ReactiveState
  ) {
    const typeofType = typeof type;
    if (typeofType === 'string' || typeofType === 'function') {
      return true;
    }
  }
  return false;
}
